import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Pane, Heading, Text, Button, toaster, Spinner, SearchInput,
  IconButton, ChevronDownIcon, ChevronRightIcon,
  PlusIcon, MinusIcon, FullscreenIcon
} from 'evergreen-ui';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import CoverageTree from './CoverageTree';
import CoverageNodeDialog from './CoverageNodeDialog';
import CoverageNodeDetail from './CoverageNodeDetail';

const CoverageMap = () => {
  const { currentProject } = useProject();
  const [nodes, setNodes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canWrite, setCanWrite] = useState(false);

  const [dialogShown, setDialogShown] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [parentIdForNew, setParentIdForNew] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandAll, setExpandAll] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const mapContainerRef = useRef(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const handleMouseDown = useCallback((e) => {
    // Don't drag when clicking interactive elements
    const tag = e.target.tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'A') return;
    if (e.target.closest('button, input, textarea, a, [role="button"]')) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    e.preventDefault();
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasDragged.current = true;
    setPan({ x: dragStart.current.panX + dx, y: dragStart.current.panY + dy });
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const fetchData = useCallback(async () => {
    if (!currentProject?.id) return;
    setLoading(true);
    try {
      const [nodesRes, membersRes] = await Promise.all([
        api.get(`/projects/${currentProject.id}/coverage`),
        api.get(`/projects/${currentProject.id}/members`).catch(() => ({ data: [] })),
      ]);
      setNodes(nodesRes.data.nodes || nodesRes.data || []);
      setMembers(membersRes.data || []);
      setCanWrite(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setCanWrite(false);
      } else {
        toaster.danger('Failed to load coverage data');
      }
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (selectedNode) {
      const updated = nodes.find(n => n.id === selectedNode.id);
      if (updated) setSelectedNode(updated);
      else setSelectedNode(null);
    }
  }, [nodes]); // intentionally omit selectedNode to avoid infinite loop

  // Filter nodes
  const filteredNodes = useMemo(() => {
    let result = nodes;
    if (statusFilter !== 'all') {
      const matchIds = new Set();
      // Include matching nodes and all their ancestors
      const parentMap = {};
      nodes.forEach(n => { parentMap[n.id] = n.parent_id; });
      nodes.forEach(n => {
        if (n.status === statusFilter) {
          let id = n.id;
          while (id) { matchIds.add(id); id = parentMap[id]; }
        }
      });
      result = result.filter(n => matchIds.has(n.id));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchIds = new Set();
      const parentMap = {};
      result.forEach(n => { parentMap[n.id] = n.parent_id; });
      result.forEach(n => {
        if (
          n.name.toLowerCase().includes(q) ||
          (n.url && n.url.toLowerCase().includes(q)) ||
          (n.http_method && n.http_method.toLowerCase().includes(q)) ||
          (n.notes && n.notes.toLowerCase().includes(q))
        ) {
          let id = n.id;
          while (id) { matchIds.add(id); id = parentMap[id]; }
        }
      });
      result = result.filter(n => matchIds.has(n.id));
    }
    return result;
  }, [nodes, statusFilter, searchQuery]);

  const handleAddRoot = () => {
    setEditingNode(null);
    setParentIdForNew(null);
    setDialogShown(true);
  };

  const handleAddChild = (parentId) => {
    setEditingNode(null);
    setParentIdForNew(parentId);
    setDialogShown(true);
  };

  const handleEdit = (node) => {
    setEditingNode(node);
    setParentIdForNew(null);
    setDialogShown(true);
  };

  const handleDelete = async (node) => {
    if (!window.confirm(`Delete "${node.name}" and all its children?`)) return;
    try {
      await api.delete(`/projects/${currentProject.id}/coverage/${node.id}`);
      toaster.success(`"${node.name}" deleted`);
      fetchData();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to delete node');
    }
  };

  const handleConfirm = async (data) => {
    try {
      if (editingNode) {
        await api.put(`/projects/${currentProject.id}/coverage/${editingNode.id}`, data);
        toaster.success('Node updated');
      } else {
        const payload = { ...data, parent_id: parentIdForNew || null };
        await api.post(`/projects/${currentProject.id}/coverage`, payload);
        toaster.success('Node created');
      }
      setDialogShown(false);
      fetchData();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to save node');
    }
  };

  const handleReorder = async (nodeId, direction) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const siblings = nodes
      .filter(n => n.parent_id === node.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = siblings.findIndex(s => s.id === nodeId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const reorderData = siblings.map((s, i) => {
      if (i === idx) return { node_id: s.id, sort_order: swapIdx };
      if (i === swapIdx) return { node_id: s.id, sort_order: idx };
      return { node_id: s.id, sort_order: i };
    });
    try {
      await api.put(`/projects/${currentProject.id}/coverage/reorder`, reorderData);
      fetchData();
    } catch (err) {
      toaster.danger('Failed to reorder');
    }
  };

  if (!currentProject) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="60vh">
        <Pane textAlign="center">
          <Text size={500} color="muted">Select a project to view coverage</Text>
        </Pane>
      </Pane>
    );
  }

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Spinner size={32} />
      </Pane>
    );
  }

  const totalNodes = nodes.length;
  const testedNodes = nodes.filter(n => n.status === 'tested' || n.status === 'vulnerable').length;
  const vulnerableNodes = nodes.filter(n => n.status === 'vulnerable').length;
  const inProgressNodes = nodes.filter(n => n.status === 'in_progress').length;
  const notTestedNodes = nodes.filter(n => n.status === 'not_tested').length;
  const overallPct = totalNodes > 0 ? Math.round((testedNodes / totalNodes) * 100) : 0;

  return (
    <Pane padding={20}>
      {/* Header */}
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={20}>
        <Pane display="flex" alignItems="baseline" gap={12}>
          <Heading size={600}>Coverage Map</Heading>
          {totalNodes > 0 && (
            <Text size={300} color="muted">{totalNodes} nodes</Text>
          )}
        </Pane>
        {canWrite && (
          <Button appearance="primary" onClick={handleAddRoot} height={36}>
            + Add Root Node
          </Button>
        )}
      </Pane>

      {/* Stats Cards */}
      {totalNodes > 0 && (
        <Pane display="flex" gap={12} marginBottom={20} flexWrap="wrap">
          {/* Progress bar card */}
          <Pane
            flex={2}
            minWidth={280}
            background="white"
            borderRadius={8}
            padding={16}
            border="default"
          >
            <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={8}>
              <Text size={300} fontWeight={600} color="#474d66">Overall Coverage</Text>
              <Text size={500} fontWeight={700} color={overallPct >= 80 ? '#52BD94' : overallPct >= 40 ? '#FFB020' : '#D14343'}>
                {overallPct}%
              </Text>
            </Pane>
            <Pane height={8} background="#edeff5" borderRadius={4} overflow="hidden">
              <Pane
                height="100%"
                borderRadius={4}
                width={`${overallPct}%`}
                style={{
                  background: overallPct >= 80
                    ? 'linear-gradient(90deg, #52BD94, #429777)'
                    : overallPct >= 40
                      ? 'linear-gradient(90deg, #FFB020, #E6991A)'
                      : 'linear-gradient(90deg, #D14343, #B83535)',
                  transition: 'width 0.6s ease',
                }}
              />
            </Pane>
            <Pane display="flex" gap={16} marginTop={10}>
              <Text size={200} color="muted">{testedNodes} tested</Text>
              <Text size={200} color="muted">{inProgressNodes} in progress</Text>
              <Text size={200} color="muted">{notTestedNodes} remaining</Text>
            </Pane>
          </Pane>

          {/* Stat mini-cards */}
          {[
            { label: 'Vulnerable', value: vulnerableNodes, color: '#D14343', bg: '#fdf4f4' },
            { label: 'Tested', value: testedNodes, color: '#52BD94', bg: '#f3faf7' },
            { label: 'In Progress', value: inProgressNodes, color: '#3366FF', bg: '#f3f6ff' },
          ].map(stat => (
            <Pane
              key={stat.label}
              flex={1}
              minWidth={120}
              background={stat.bg}
              borderRadius={8}
              padding={16}
              border="default"
              cursor="pointer"
              onClick={() => setStatusFilter(
                statusFilter === stat.label.toLowerCase().replace(' ', '_')
                  ? 'all'
                  : stat.label.toLowerCase().replace(' ', '_')
              )}
              style={{
                borderColor: statusFilter === stat.label.toLowerCase().replace(' ', '_') ? stat.color : undefined,
                borderWidth: statusFilter === stat.label.toLowerCase().replace(' ', '_') ? 2 : 1,
              }}
            >
              <Text size={200} color="muted" display="block">{stat.label}</Text>
              <Text size={600} fontWeight={700} color={stat.color}>{stat.value}</Text>
            </Pane>
          ))}
        </Pane>
      )}

      {/* Toolbar */}
      {totalNodes > 0 && (
        <Pane
          display="flex"
          alignItems="center"
          gap={12}
          marginBottom={16}
          flexWrap="wrap"
        >
          <SearchInput
            placeholder="Search nodes, URLs, notes..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            width={260}
            height={32}
          />
          <Pane flex={1} />
          <Pane display="flex" alignItems="center" gap={4} background="white" borderRadius={6} border="1px solid #E6E8F0" paddingX={4} paddingY={2}>
            <IconButton icon={MinusIcon} appearance="minimal" size="small" onClick={() => setZoom(z => Math.max(0.25, z - 0.15))} title="Zoom out" />
            <Text size={200} fontFamily="mono" color="#696F8C" style={{ minWidth: 36, textAlign: 'center', userSelect: 'none' }}>
              {Math.round(zoom * 100)}%
            </Text>
            <IconButton icon={PlusIcon} appearance="minimal" size="small" onClick={() => setZoom(z => Math.min(2, z + 0.15))} title="Zoom in" />
            <IconButton icon={FullscreenIcon} appearance="minimal" size="small" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Reset view" />
          </Pane>
          <IconButton
            icon={expandAll ? ChevronDownIcon : ChevronRightIcon}
            appearance="minimal"
            onClick={() => setExpandAll(!expandAll)}
            title={expandAll ? 'Collapse all' : 'Expand all'}
          />
          {statusFilter !== 'all' && (
            <Button
              size="small"
              appearance="minimal"
              onClick={() => setStatusFilter('all')}
            >
              Clear filter
            </Button>
          )}
        </Pane>
      )}

      {/* Main content */}
      <Pane display="flex" gap={16} alignItems="flex-start">
        {/* Mind map panel */}
        <Pane
          ref={mapContainerRef}
          flex={1}
          minWidth={0}
          background="#FAFBFF"
          borderRadius={10}
          border="1px solid #E6E8F0"
          overflow="hidden"
          style={{
            backgroundImage: 'radial-gradient(circle, #E6E8F0 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            cursor: isDragging.current ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onWheel={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              setZoom(z => Math.min(2, Math.max(0.25, z + (e.deltaY > 0 ? -0.08 : 0.08))));
            }
          }}
        >
          {nodes.length === 0 ? (
            <Pane textAlign="center" padding={64}>
              <Pane marginBottom={16}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#8f95b2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v18M3 12h18M7 7l10 10M17 7L7 17" opacity="0.3" />
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </Pane>
              <Heading size={500} marginBottom={8} color="#474d66">No Coverage Nodes Yet</Heading>
              <Text color="muted" display="block" marginBottom={16}>
                Build your testing coverage map by adding nodes for each feature, endpoint, or component.
              </Text>
              {canWrite && (
                <Pane display="flex" gap={8} justifyContent="center">
                  <Button appearance="primary" onClick={handleAddRoot}>
                    + Add First Node
                  </Button>
                </Pane>
              )}
            </Pane>
          ) : filteredNodes.length === 0 ? (
            <Pane textAlign="center" padding={48}>
              <Text color="muted">No nodes match your filter.</Text>
              <Button size="small" appearance="minimal" onClick={() => { setSearchQuery(''); setStatusFilter('all'); }} marginTop={8}>
                Clear filters
              </Button>
            </Pane>
          ) : (
            <Pane
              padding={20}
              minHeight={300}
            >
              <Pane
                onClick={e => {
                  // Suppress click after dragging so nodes don't get selected
                  if (hasDragged.current) { e.stopPropagation(); hasDragged.current = false; }
                }}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'top left',
                  display: 'inline-block',
                }}
              >
                <CoverageTree
                  nodes={filteredNodes}
                  onAdd={handleAddChild}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onReorder={handleReorder}
                  onSelect={setSelectedNode}
                  selectedNodeId={selectedNode?.id}
                  canWrite={canWrite}
                  expandAll={expandAll}
                  searchQuery={searchQuery}
                />
              </Pane>
            </Pane>
          )}
        </Pane>

        {/* Detail panel */}
        {selectedNode && (
          <Pane
            width={420}
            flexShrink={0}
            background="white"
            borderRadius={10}
            border="1px solid #E6E8F0"
            display="flex"
            flexDirection="column"
            height="calc(100vh - 32px)"
            style={{ alignSelf: 'flex-start', position: 'sticky', top: 16, overflow: 'hidden' }}
          >
            <CoverageNodeDetail
              node={selectedNode}
              projectId={currentProject.id}
              canWrite={canWrite}
              onClose={() => setSelectedNode(null)}
              onRefresh={fetchData}
            />
          </Pane>
        )}
      </Pane>

      <CoverageNodeDialog
        isShown={dialogShown}
        onClose={() => setDialogShown(false)}
        onConfirm={handleConfirm}
        node={editingNode}
        members={members}
      />
    </Pane>
  );
};

export default CoverageMap;
